import {convertPxToRem} from '@pb/kit-util-convert-px-to-rem';

import MultiColor from './icons-multi-color-generated';
import SingleColor from './icons-single-color-generated';

/**
 * Merged enum of all available icon names — both single-color (font glyph)
 * and multi-color (inline SVG) icons in the PitchBook icon set.
 *
 * Pass any value from this object as the `type` prop of `Icon`. The component
 * automatically chooses the correct rendering path (font vs. SVG).
 *
 * @example
 * ```tsx
 * import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
 *
 * <Icon type={Icons.SEARCH} size={Sizes.Size_16} />
 * <Icon type={Icons.EXCEL} size={Sizes.Size_24} /> // multi-color SVG
 * ```
 */
export const Icons = {
    ...SingleColor,
    ...MultiColor,
} as const;

/**
 * Available pixel sizes for the `Icon` component, expressed as string tokens
 * that map to `rem` values computed from the design system's base font size.
 *
 * Use these constants rather than raw numbers to keep icon sizes consistent
 * with the rest of the design system.
 *
 * | Token | Pixels |
 * |-------|--------|
 * | `Size_8` | 8 px |
 * | `Size_10` | 10 px |
 * | `Size_12` | 12 px |
 * | `Size_14` | 14 px |
 * | `Size_16` | 16 px (default rendering size) |
 * | `Size_19` | 19 px |
 * | `Size_24` | 24 px |
 * | `Size_28` | 28 px |
 * | `Size_32` | 32 px |
 *
 * @example
 * ```tsx
 * <Icon type={Icons.SEARCH} size={Sizes.Size_14} />
 * ```
 */
export const Sizes = {
    Size_8: 'Size_8',
    Size_10: 'Size_10',
    Size_12: 'Size_12',
    Size_14: 'Size_14',
    Size_16: 'Size_16',
    Size_19: 'Size_19',
    Size_24: 'Size_24',
    Size_28: 'Size_28',
    Size_32: 'Size_32',
} as const;
export const SIZES_TYPES = {
    [Sizes.Size_8]: convertPxToRem(8),
    [Sizes.Size_10]: convertPxToRem(10),
    [Sizes.Size_12]: convertPxToRem(12),
    [Sizes.Size_14]: convertPxToRem(14),
    [Sizes.Size_16]: convertPxToRem(16),
    [Sizes.Size_19]: convertPxToRem(19),
    [Sizes.Size_24]: convertPxToRem(24),
    [Sizes.Size_28]: convertPxToRem(28),
    [Sizes.Size_32]: convertPxToRem(32),
} as const;

/**
 * Union of all valid icon name strings — every value from both the single-color
 * (font glyph) and multi-color (inline SVG) icon sets.
 *
 * Use this type to annotate any prop or variable that holds an icon name when
 * you want strict type safety beyond a plain `string`. In practice, most
 * consumers pass a value from the `Icons` object, which already satisfies this
 * type.
 */
export type IconType = typeof SingleColor[keyof typeof SingleColor] | typeof MultiColor[keyof typeof MultiColor];

/**
 * Union of the `Sizes` object keys (`"Size_8"` | `"Size_10"` | … | `"Size_32"`).
 *
 * Pass one of these tokens as the `size` prop of `Icon`. The component converts
 * the token to a `rem` value at render time using the design system's base font
 * size. Using the token instead of a raw pixel value keeps icon sizes consistent
 * with the rest of the design system and respects user font-size preferences.
 */
export type IconSize = keyof typeof Sizes;
